<script setup>
// FLOW-01, revisão de UX (M1) — os dados do teste como formulário, no lugar do JSON cru: um campo
// por parâmetro do `params` do YAML e um por chave do contexto antigo. Mora na gaveta "Dados do
// teste" (`GavetaDosDados.vue`) e na caixa do gatilho, que escrevem no MESMO estado
// (`dadosDoTeste.js`): cada campo reescreve o JSON, e por isso trocar para o JSON não perde nada.
//
// Revisão por delta do UX2 (P6): a alternância Formulário | JSON da caixa do gatilho mora aqui
// (`parte="modo"`), com botões de `aria-pressed`.
//
// FLOW-12 (Task 5.1, T1, G7): os params são o formulário da tabela do T1, um `CampoDoTeste` por
// param, na ordem do YAML, com o `object` (agrupado sob o nome, ou no JSON sem `properties`), o
// toggle do `boolean` e a lista de uma linha por item. As regras são conferidas no campo ao sair dele,
// com os textos do servidor (`erroDoParam`); o obrigatório vazio, com o texto da SPEC. O param
// opcional vazio é o que o agente não mandaria, e não é erro (`semOpcionaisVazios`). `conferir()`
// mostra os erros de todos os campos e põe o foco no primeiro errado: é o "Executar" da gaveta.
//
// FLOW-06 (Task 6.5): o erro que vem de fora (a recusa do servidor ao "Executar agora" que nomeia um
// param, em `errosDeFora`) aparece no campo dele, na frente do que o formulário confere. Quem monta o
// formulário o tira quando a pessoa mexe; sem ele, nada muda.
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import AvisoDoFlow from './AvisoDoFlow.vue';
import CampoDoTeste from './CampoDoTeste.vue';
import { erroDoParam, semOpcionaisVazios } from './regrasDoParametro';

const props = defineProps({
  // O estado de `criarDadosDoTeste`.
  dados: { type: Object, required: true },
  // 'params' | 'contexto', ou 'modo': a alternância Formulário | JSON, no topo dos dados.
  parte: { type: String, default: 'params' },
  // O começo dos ids dos campos, único na página: a gaveta e a caixa do gatilho podem estar
  // abertas juntas.
  prefixo: { type: String, default: 'teste' },
  // `{nome do param: mensagem}`: o erro que o servidor deu a um param.
  errosDeFora: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const raiz = useTemplateRef('raiz');

const ehObjeto = v => !!v && typeof v === 'object' && !Array.isArray(v);
const lido = computed(() =>
  props.parte === 'params' ? props.dados.params : props.dados.contexto
);

// O JSON quebrado, dito como o modo JSON o diz, para a pessoa achar o mesmo erro lá.
const erroDoJson = computed(() => {
  const { erro, detalhe } = lido.value;
  if (erro === 'INVALID_JSON') {
    return t('POWER_TOOLS.FLOW_EDITOR.TEST.INPUT.INVALID_JSON', { detalhe });
  }
  return erro ? t('POWER_TOOLS.FLOW_EDITOR.TEST.INPUT.NOT_OBJECT') : '';
});

// -- os params (T1)

// Os params que já mostram erro: o campo de onde a pessoa saiu, ou todos depois do `conferir()`.
const tocados = ref(new Set());
const todos = ref(false);
const sair = nome => {
  tocados.value = new Set([...tocados.value, nome]);
};

const campos = computed(() => {
  if (lido.value.erro) return [];
  const valores = lido.value.valor;
  const definicoes = ehObjeto(props.dados.definicoes)
    ? props.dados.definicoes
    : {};
  const conferidos = semOpcionaisVazios(definicoes, valores);
  const nomes = [
    ...new Set([...Object.keys(definicoes), ...Object.keys(valores)]),
  ];
  return nomes.map(nome => {
    const def = ehObjeto(definicoes[nome]) ? definicoes[nome] : null;
    const valor = valores[nome];
    const erro = def
      ? erroDoParam(def, conferidos[nome], nome)
      : {
          campo: nome,
          caminho: [nome],
          mensagem: t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.NOT_A_PARAM', {
            nome,
          }),
        };
    const deFora = props.errosDeFora[nome];
    return {
      nome,
      def,
      valor,
      obrigatorio: !!def && (def.type === 'object' || def.required !== false),
      erro,
      erroDeFora: deFora
        ? { campo: nome, caminho: [nome], mensagem: deFora }
        : null,
      mostrar: todos.value || tocados.value.has(nome),
    };
  });
});

// O foco no campo do erro: o do caminho dele, ou o do campo mais perto que existe na tela (o item
// que a lista ainda não desenhou, a propriedade de um JSON).
function focarNoErro(erro) {
  for (let n = erro.caminho.length; n > 0; n -= 1) {
    const id = `${props.prefixo}-campo-${erro.caminho
      .slice(0, n)
      .map(String)
      .join('-')}`.replace(/[^\w-]/g, '_');
    const alvo = raiz.value?.querySelector(`[id="${id}"]`);
    if (alvo) {
      alvo.focus();
      return;
    }
  }
}

// "Executar" com erro (T1, G13): todos os campos mostram o erro, e o foco vai ao primeiro errado.
// Devolve o primeiro erro, ou nulo quando tudo passa.
async function conferir() {
  todos.value = true;
  const primeiro = campos.value.find(campo => campo.erro)?.erro || null;
  if (primeiro) {
    await nextTick();
    focarNoErro(primeiro);
  }
  return primeiro;
}

// -- o contexto antigo (o `context_map`): uma chave por linha, em texto

const camposDoContexto = computed(() => {
  if (lido.value.erro || props.parte !== 'contexto') return [];
  const valores = lido.value.valor;
  return [
    ...new Set([...props.dados.chavesDeContexto, ...Object.keys(valores)]),
  ].map(nome => {
    const valor = valores[nome];
    let texto = '';
    if (typeof valor === 'string') texto = valor;
    else if (valor !== undefined) texto = JSON.stringify(valor);
    return { nome, texto };
  });
});
const idDoContexto = nome => `${props.prefixo}-contexto-${nome}`;

const MODOS = [
  {
    label: t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.MODE_FORM'),
    modo: 'formulario',
    attrs: { 'data-modo': 'formulario' },
  },
  {
    label: t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.MODE_JSON'),
    modo: 'json',
    attrs: { 'data-modo': 'json' },
  },
];

defineExpose({ conferir });
</script>

<template>
  <div
    v-if="parte === 'modo'"
    role="group"
    :aria-label="t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.MODE')"
    class="self-start"
  >
    <TabBar
      compact
      :tabs="MODOS"
      :initial-active-tab="dados.modo === 'json' ? 1 : 0"
      @tab-changed="dados.mudarModo($event.modo)"
    />
  </div>
  <AvisoDoFlow v-else-if="lido.erro" role="alert" data-json-quebrado>
    <span>
      {{ erroDoJson }}
      {{ t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.FIX_IN_JSON') }}
    </span>
    <button
      type="button"
      class="p-0 font-semibold underline"
      data-abrir-json
      @click="dados.mudarModo('json')"
    >
      {{ t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.OPEN_JSON') }}
    </button>
  </AvisoDoFlow>
  <template v-else-if="parte === 'contexto'">
    <p v-if="!camposDoContexto.length" class="m-0 text-xs text-n-slate-11">
      {{ t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.NO_CONTEXT') }}
    </p>
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="campo in camposDoContexto"
        :key="campo.nome"
        class="flex flex-col gap-1.5"
        :data-campo-do-contexto-antigo="campo.nome"
      >
        <label
          :for="idDoContexto(campo.nome)"
          class="font-mono text-sm font-medium text-n-slate-12"
        >
          {{ campo.nome }}
        </label>
        <Input
          :id="idDoContexto(campo.nome)"
          :model-value="campo.texto"
          size="sm"
          spellcheck="false"
          data-campo
          @update:model-value="valor => dados.editarContexto(campo.nome, valor)"
        />
      </div>
    </div>
  </template>
  <p v-else-if="!campos.length" class="m-0 text-xs text-n-slate-11">
    {{ t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.NO_PARAMS') }}
  </p>
  <div v-else ref="raiz" class="flex flex-col gap-4" data-campos-do-teste>
    <CampoDoTeste
      v-for="campo in campos"
      :key="campo.nome"
      :def="campo.def"
      :valor="campo.valor"
      :caminho="[campo.nome]"
      :rotulo="campo.nome"
      :obrigatorio="campo.obrigatorio"
      :erro="campo.erroDeFora || (campo.mostrar ? campo.erro : null)"
      :prefixo="prefixo"
      :data-param="campo.nome"
      @update:valor="valor => dados.editarParam(campo.nome, valor)"
      @sair="sair(campo.nome)"
    />
  </div>
</template>
